/**
 * Utility functions for responsive hero images
 * Works with static files in /public/img/ with naming pattern: {baseName}-{width}.{ext}
 */

// Hero image widths matching IMAGE_TYPES.hero configuration
export const HERO_WIDTHS = [640, 1280, 1920, 2560, 3840, 5120];

export interface HeroImageUrls {
  '640_avif'?: string;
  '640_webp'?: string;
  '640_jpeg'?: string;
  '1280_avif'?: string;
  '1280_webp'?: string;
  '1280_jpeg'?: string;
  '1920_avif'?: string;
  '1920_webp'?: string;
  '1920_jpeg'?: string;
  '2560_avif'?: string;
  '2560_webp'?: string;
  '2560_jpeg'?: string;
  '3840_avif'?: string;
  '3840_webp'?: string;
  '3840_jpeg'?: string;
  '5120_avif'?: string;
  '5120_webp'?: string;
  '5120_jpeg'?: string;
}

/**
 * Generate srcset string for a specific format
 * @param imageUrls - The image URLs object
 * @param format - The format to use ('avif', 'webp', or 'jpeg')
 * @returns srcset string for use in img/source elements
 */
export function generateHeroSrcSet(
  imageUrls: HeroImageUrls,
  format: 'avif' | 'webp' | 'jpeg'
): string {
  const srcSetParts: string[] = [];
  
  for (const width of HERO_WIDTHS) {
    const key = `${width}_${format}` as keyof HeroImageUrls;
    const url = imageUrls[key];
    if (url) {
      srcSetParts.push(`${url} ${width}w`);
    }
  }
  
  return srcSetParts.join(', ');
}

/**
 * Get fallback URL (largest JPEG or original)
 * @param imageUrls - The image URLs object
 * @param baseName - Base name of the image (e.g., "hero-lagoon")
 * @param originalSrc - Original image path as fallback
 * @returns URL to use as fallback
 */
export function getHeroFallbackUrl(
  imageUrls: HeroImageUrls,
  baseName: string,
  originalSrc: string
): string {
  // Try to get largest JPEG (5120)
  const largestJpeg = imageUrls['5120_jpeg'];
  if (largestJpeg) {
    return largestJpeg;
  }
  
  // Fallback to original image
  return originalSrc;
}

/**
 * Build image URLs for static /public/img/ files
 * @param baseName - Base name like "hero-lagoon", "food-hall", "aerial-1"
 * @returns Object with all format/width combinations
 */
export function buildStaticHeroImageUrls(baseName: string): HeroImageUrls {
  const urls: HeroImageUrls = {};
  
  for (const width of HERO_WIDTHS) {
    urls[`${width}_avif` as keyof HeroImageUrls] = `/img/${baseName}-${width}.avif`;
    urls[`${width}_webp` as keyof HeroImageUrls] = `/img/${baseName}-${width}.webp`;
    urls[`${width}_jpeg` as keyof HeroImageUrls] = `/img/${baseName}-${width}.jpg`;
  }
  
  return urls;
}
